import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavigateFunction, useNavigate, useParams } from "react-router-dom";
import { Card, Grid, Typography, useMediaQuery } from "@mui/material";
import { SearchOutlined } from "@mui/icons-material";
import DetailCard from "../../../components/card/DetailCard";
import InputTextField from "../../../components/input/inputTextField";
import { debouncedApiCall } from "../../../utils/app.utils";
import { setLoading, unsetLoading } from "../../../store/loading/loading.slice";
import {
  alertActive,
  alertError,
  alertSuccess,
} from "../../../components/alert/swal";
import { advisorApi } from "../../../api/advisor/advisor.api";
import { advisorRequestApi } from "../../../api/advisor-request/advisor-request.api";
import NavigateListCustom from "../../../components/navigation/NavigationListCustom";
import { routeName } from "../../../routes/routeName";
import { colors } from "../../../constants/colors";
import {
  ButtonOutlined,
  ButtonPrimary,
} from "../../../components/button/ButtonCustoms";
import ModalSubmitTransferAdvisor from "./components/ModalSubmitTransferAdvisor";
import { useTranslation } from "react-i18next";

type Props = {};

const TransferAdvisor: React.FC<Props> = (props: Props) => {
  const { t } = useTranslation();
  const navigate: NavigateFunction = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  const matches = useMediaQuery("(min-width:1440px)");
  const [dataAdvisor, setDataAdvisor] = useState<any[]>([]);
  const [search, setSearch] = useState<string>("");

  const [page, setPage] = useState<number>(1);
  const [pageLimit, setPageLimit] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [sortBy, setSortBy] = useState<string>("");
  const [sortType, setSortType] = useState<string>("ASC");

  const open = useSelector((state: any) => state?.navbar?.open);
  const [isShowModalSendRequest, setIsShowModalSendRequest] =
    useState<boolean>(false);

  const [file, setFile] = useState<File | null>(null); // For handling file selection
  const [formSendRequest, setFormSendRequest] = useState<{
    advisor_request_name: string;
    advisor_request_desc: string;
    id_advisor: number;
  }>({
    advisor_request_name: "",
    advisor_request_desc: "",
    id_advisor: 0,
  });

  const [dataRequest, setDataRequest] = useState<any>({});
  const [dataRequestTransfer, setDataRequestTransfer] = useState<any>({});
  // const status_reponse: any = "reject";
  const [isOpenModalTransfer, setIsOpenModalTransfer] =
    useState<boolean>(false);
  const [inputComment, setInputComment] = useState<string>("");

  const getDataAdvisor = async () => {
    let query: any = {
      page: page,
      limit: pageLimit,
      sortBy: sortBy,
      sortType: sortType,
    };

    if (search) query = { ...query, leave_type_name: search };

    const res = await advisorApi.getAllAdvisor(query);
    const data = res.data;
    if (data.status < 300) {
      setDataAdvisor(data.data.items);
      setTotalItems(res.data?.data?.meta?.totalItems || 0);
    } else {
      setDataAdvisor([]);
    }
  };

  useEffect(() => {
    dispatch(setLoading());
    setTimeout(() => {
      dispatch(unsetLoading());
    }, 500);
  }, []);

  useEffect(() => {
    getDataAdvisor();
  }, [page, pageLimit, sortBy, sortType, search]);

  const getSingleRequest = async () => {
    const res = await advisorRequestApi.getAdvisorRequestByUserLogin();
    const data = res.data;
    if (data.status < 300) {
      setDataRequest(data.data);
    }
  };

  useEffect(() => {
    getSingleRequest();
  }, []);

  const handleStatus = (status: string) => {
    if (status === "รอพิจารณา") { // i18n-ignore
      return "pending";
    } else if (status === "ตอบรับเป็นที่ปรึกษา") { // i18n-ignore
      return "approve";
    } else if (status === "ปฏิเสธคำขอ") { // i18n-ignore
      return "reject";
    } else {
      return "default";
    }
  };

  const handleSave = async (
    advisor_request_name: string,
    advisor_request_desc: string,
    id_advisor: number,
    newFile?: File
  ) => {
    if (newFile && advisor_request_name && advisor_request_desc && id_advisor) {
      const formData = new FormData();

      formData.append("file", newFile);
      formData.append("advisor_request_name", advisor_request_name);
      formData.append("advisor_request_desc", advisor_request_desc);
      formData.append("id_advisor", id_advisor.toString());

      try {
        dispatch(unsetLoading());
        const response = await advisorRequestApi.createAdvisorRequest(formData);
        if (response.status < 300) {
          alertSuccess(
            t("ส่งคำร้องขอ"),
            t("ส่งคำร้องขอ เรียบร้อยแล้ว"),
            undefined,
            () => {
              setIsShowModalSendRequest(false);
              setFormSendRequest({
                advisor_request_name: "",
                advisor_request_desc: "",
                id_advisor: 0,
              });
              getSingleRequest();
              setFile(null);
            }
          );
        }
      } catch (error) {
        alertError(t("ส่งคำร้องขอ ไม่สำเร็จ"));
      } finally {
        dispatch(unsetLoading());
      }
    }
  };

  const handleCancelRequest = async (id: number) => {
    if (id) {
      alertActive(
        t("ยกเลิกส่งคำร้องขอ"),
        t("ท่านต้องการยกเลิกส่งคำร้องขอ ใช่หรือไม่"),
        undefined,
        undefined,
        async () => {
          dispatch(setLoading());
          await advisorRequestApi
            .deleteAdvisorRequest(+id)
            .then((res) => {
              if (res.status < 300) {
                alertSuccess(
                  t("ยกเลิกส่งคำร้องขอ เรียบร้อย"),
                  undefined,
                  undefined,
                  () => {
                    getSingleRequest();
                    getDataAdvisor();
                  }
                );
              }
            })
            .catch(() => {
              alertError(t("ส่งคำร้องขอ ไม่สำเร็จ"));
            })
            .finally(() => {
              dispatch(unsetLoading());
            });
        }
      );
    }
    return;
  };

  const handleTransferAdvisorRequest = async (
    id_advisor_request: number,
    new_advisor_id: number,
    comment: string
  ) => {
    if (id_advisor_request && new_advisor_id && comment) {
      await advisorRequestApi
        .transferAdvisorRequest(
          +id_advisor_request,
          new_advisor_id,
          comment ?? ""
        )
        .then((res) => {
          if (res.status < 300) {
            alertSuccess(
              t("ส่งคำร้องขอเปลี่ยนอาจารย์ที่ปรึกษา"),
              t("ส่งคำร้องขอเปลี่ยนอาจารย์ที่ปรึกษา เรียบร้อบแล้ว"),
              undefined,
              () => {
                navigate(routeName.student.chooseAdvisor);
                setIsOpenModalTransfer(false);
                setInputComment("");
              }
            );
          }
        })
        .catch(() => {
          alertError(t("ส่งคำร้องขอเปลี่ยนอาจารย์ที่ปรึกษา ไม่สำเร็จ"));
        })
        .finally(() => {
          dispatch(unsetLoading());
        });
    } else {
      alertError(t("ข้อมูลไม่ครบถ้วน กรุณาตรวจสอบอีกครั้ง"));
    }
    return;
  };

  return (
    <div>
      <ModalSubmitTransferAdvisor
        setIsOpenModal={(isOpen: boolean) => setIsOpenModalTransfer(isOpen)}
        isOpenModal={isOpenModalTransfer}
        onSubmit={() => {
          if (
            id &&
            inputComment &&
            dataRequestTransfer &&
            dataRequestTransfer.id_advisor
          ) {
            handleTransferAdvisorRequest(
              +id,
              +dataRequestTransfer.id_advisor,
              inputComment
            );
          }
        }}
        inputComment={inputComment}
        setInputComment={(value: string) => setInputComment(value)}
      />

      <NavigateListCustom
        navigateData={[
          {
            name: t("อาจารย์ที่ปรึกษา"),
            key: "1",
            pathName: routeName.student.chooseAdvisor,
          },
          {
            name: t("ส่งคำร้องขอเปลี่ยนอาจารย์ที่ปรึกษาวิทยานิพนธ์"),
            key: "2",
            pathName: routeName.student.TransferAdvisor + "/" + id,
          },
        ]}
      />

      <section className="w-full mb-12">
        <Typography variant="h3">
          {t("ส่งคำร้องขอเปลี่ยนอาจารย์ที่ปรึกษาวิทยานิพนธ์")}
        </Typography>
      </section>

      <section className="w-full mb-12">
        <Grid container spacing={2}>
          <Grid
            item
            lg={12}
            md={12}
            sm={12}
            xs={12}
            sx={{
              alignSelf: "end",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <DetailCard
              fullname={dataRequest?.first_name + " " + dataRequest?.last_name}
              email={dataRequest?.email_organization ?? ""}
              maxStudents={dataRequest?.number_students ?? ""}
              requestCount={dataRequest?.count_req_all ?? ""}
              advisorCount={dataRequest?.count_req ?? ""}
              isSelect={false}
              status={"transfer_1"}
              setIsShowModalSendRequest={setIsShowModalSendRequest}
              remark={""}
              degree={dataRequest?.degree ?? ""}
              faculty={dataRequest?.faculty ?? ""}
              year={dataRequest?.year ?? ""}
              classRoom={dataRequest?.classRoom ?? ""}
              onCancelRequest={() => {
                handleCancelRequest(dataRequest?.id_advisor_request);
              }}
              image={dataRequest?.file_path_img_emp ?? ""}
              isTransfer={true}
            />
            <section className="px-4">
              <ButtonPrimary
                onClick={() => {}}
                mode={"transfer"}
                buttonName={""}
                btnProps={{
                  padding: "10px 0px 10px 10px",
                }}
              />
            </section>

            {dataRequestTransfer && dataRequestTransfer.id_advisor ? (
              <DetailCard
                fullname={
                  dataRequestTransfer?.first_name +
                  " " +
                  dataRequestTransfer?.last_name
                }
                email={dataRequestTransfer?.email_organization ?? ""}
                maxStudents={dataRequestTransfer?.number_students ?? ""}
                requestCount={dataRequestTransfer?.count_req_all ?? ""}
                advisorCount={dataRequestTransfer?.count_req ?? ""}
                isSelect={false}
                status={"transfer_2"}
                setIsShowModalSendRequest={setIsShowModalSendRequest}
                remark={""}
                degree={dataRequestTransfer?.degree ?? ""}
                faculty={dataRequestTransfer?.faculty ?? ""}
                year={dataRequestTransfer?.year ?? ""}
                classRoom={dataRequestTransfer?.classRoom ?? ""}
                onCancelRequest={() => {}}
                image={dataRequestTransfer?.file_path ?? ""}
                isTransfer={true}
              />
            ) : (
              <Card
                sx={{
                  boxShadow: `0px 2px 12px 2px ${colors.subPrimary}`, // Custom shadow color
                  padding: 2,
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  borderRadius: "12px",
                  border: `2px solid ${colors.subPrimary}`, // Custom border color
                  height: "210px",
                  width: "425px",
                }}
              />
            )}
          </Grid>
        </Grid>
      </section>

      <section className="w-full mt-4">
        <>
          <section className="w-full mt-4">
            <Typography variant="h6">
              {t("กรุณาเลือกอาจารย์ที่ปรึกษาที่ท่านต้องการ")}
            </Typography>
          </section>

          <div className="w-full grid grid-cols-12 gap-4 mb-4">
            <article className="w-full col-span-12 lg:col-span-3  md:col-span-3  sm:col-span-12">
              <InputTextField
                startAdornment={<SearchOutlined />}
                placeholder={t("ค้นหาชื่อ - นามสกุล")}
                onchange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  debouncedApiCall({
                    searchItem: e.target.value,
                    setItem: setSearch,
                  })
                }
              />
            </article>
          </div>

          <Grid container spacing={2}>
            {dataAdvisor &&
              dataAdvisor
                .filter(
                  (advisor) => advisor.id_advisor !== dataRequest?.id_advisor
                )
                .map((advisor, index) => (
                  <Grid
                    key={index + 1}
                    item
                    lg={open ? 6 : 4}
                    md={open ? 6 : 4}
                    sm={12}
                    xs={12}
                    sx={{
                      alignSelf: "end",
                    }}
                    onClick={() => {
                      setFormSendRequest({
                        ...formSendRequest,
                        id_advisor: advisor.id_advisor,
                      });
                    }}
                  >
                    <DetailCard
                      fullname={advisor.first_name + " " + advisor.last_name}
                      email={advisor.email_organization ?? ""}
                      maxStudents={advisor.number_students ?? ""}
                      requestCount={advisor.count_req_all}
                      advisorCount={advisor.count_advisor_req}
                      isSelect={
                        dataRequestTransfer?.id_advisor
                          ? formSendRequest.id_advisor === advisor.id_advisor
                            ? true
                            : false
                          : true
                      }
                      status={"default"}
                      setIsShowModalSendRequest={setIsShowModalSendRequest}
                      degree={advisor.degree}
                      faculty={advisor.faculty_names}
                      year={advisor.year}
                      classRoom={advisor.classRoom}
                      image={advisor.file_path}
                      isSelectAdvisorTransfer={true}
                      onSetAdvisor={() => {
                        setDataRequestTransfer(advisor);
                      }}
                      onCancelAdvisor={() => {
                        setDataRequestTransfer({});
                      }}
                      dataRequestTransfer={
                        dataRequestTransfer?.id_advisor === advisor.id_advisor
                          ? dataRequestTransfer
                          : {}
                      }
                    />
                  </Grid>
                ))}
          </Grid>
        </>
      </section>
      {dataRequestTransfer && dataRequestTransfer.id_advisor ? (
        <section className="w-full justify-center items-center flex flex-row mt-4">
          <ButtonOutlined
            onClick={() => {
              navigate(routeName.student.chooseAdvisor);
            }}
            mode="del"
            buttonName={t("ยกเลิก")}
            btnProps={{
              width: "125px",
            }}
          />

          <ButtonPrimary
            onClick={() => {
              setIsOpenModalTransfer(true);
            }}
            mode={"next"}
            buttonName={t("ยืนยัน")}
            btnProps={{
              width: "125px",
              margin: "0 10px 0 10px",
            }}
          />
        </section>
      ) : null}
    </div>
  );
};

export default TransferAdvisor;
